import { supabase } from '@/integrations/supabase/client';

export type DeliveryRateType = 'regular' | 'express';

export interface DeliveryRateOption {
  id: string;
  name: string;
  type: DeliveryRateType;
  price: number;
  currency: string;
  estimatedDays: string;
  description: string;
  provider?: 'fez' | 'self-delivery';
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export async function getDeliveryRates(params: {
  sellerId: string;
  destinationState: string;
  destinationCity: string;
  destinationAddress?: string;
  items?: Array<{ quantity: number; price: number; title: string }>;
  weight?: number;
}): Promise<{ rates: DeliveryRateOption[]; source?: string }>{
  // Use direct fetch instead of supabase.functions.invoke to avoid auth/CORS issues
  const url = `${SUPABASE_URL}/functions/v1/get-delivery-rates`;
  
  const bodyPayload = {
    sellerId: params.sellerId,
    destinationState: params.destinationState,
    destinationCity: params.destinationCity,
    destinationAddress:
      params.destinationAddress || `${params.destinationCity}, ${params.destinationState}`,
    items: params.items,
    weight: params.weight,
  };

  console.log('[deliveryRates] Fetching rates:', bodyPayload.destinationState, bodyPayload.destinationCity);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_KEY,
    },
    body: JSON.stringify(bodyPayload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[deliveryRates] HTTP error:', response.status, errorText.substring(0, 200));
    throw new Error(`Delivery rates request failed: ${response.status}`);
  }

  const data = await response.json();
  console.log('[deliveryRates] Response:', data?.success, 'rates:', data?.rates?.length, 'source:', data?.source);

  if (!data?.success || !Array.isArray(data?.rates)) {
    throw new Error(data?.message || 'Failed to get delivery rates');
  }

  const rateSource: string = data.source || 'unknown';

  const rates: DeliveryRateOption[] = data.rates.map((r: any) => ({
    id: String(r.id),
    name: String(r.name || 'Delivery'),
    type: (r.type === 'express' ? 'express' : 'regular') as DeliveryRateType,
    price: Number(r.price || 0),
    currency: String(r.currency || 'NGN'),
    estimatedDays: String(r.estimatedDays || r.estimated_days || '3-5 business days'),
    description: String(r.description || ''),
    provider: r.provider === 'self-delivery' ? 'self-delivery' : 'fez',
  }));

  return { rates, source: rateSource };
}

// Direct Fez rate fetcher — calls get-fez-rate edge function
export async function getFezRateDirect(params: {
  sellerId: string;
  recipientState: string;
  recipientAddress?: string;
  weight?: number;
  valueOfItem?: number;
}): Promise<{ success: boolean; delivery_fee: number; source?: string }> {
  const url = `${SUPABASE_URL}/functions/v1/get-fez-rate`;

  const bodyPayload = {
    sellerId: params.sellerId,
    recipientState: params.recipientState,
    recipientAddress: params.recipientAddress || `${params.recipientState} City Center`,
    weight: params.weight || 1,
    valueOfItem: params.valueOfItem || 10000,
  };

  console.log('[getFezRateDirect] Request:', JSON.stringify(bodyPayload));

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_KEY,
      },
      body: JSON.stringify(bodyPayload),
    });

    const data = await response.json();
    console.log('[getFezRateDirect] Response:', JSON.stringify(data));

    if (data?.success && typeof data?.delivery_fee === 'number' && data.delivery_fee > 0) {
      return { success: true, delivery_fee: data.delivery_fee, source: data.source };
    }

    return { success: false, delivery_fee: 0 };
  } catch (err: any) {
    console.error('[getFezRateDirect] Error:', err?.message);
    return { success: false, delivery_fee: 0 };
  }
}
